import type { ViewportState } from '@annotorious/core';
import { colord, type AnyColor } from 'colord';
import type { RevivedTextSelectorLike, TextAnnotationLike } from '../../model';
import type { TextAnnotatorState } from '../../state';
import { 
  computeStyle,
  createRenderer,
  DEFAULT_SELECTED_STYLE,
  DEFAULT_STYLE,
  type Highlight,
  type HighlightStyle,
  type HighlightStyleExpression,
  type Painter, 
  type RendererFactory, 
  type ViewportBounds 
} from '../';

const toCSS = (s?: HighlightStyle) => {
  const backgroundColor = colord(s?.fill || DEFAULT_STYLE.fill as AnyColor)
    .alpha((s?.fillOpacity === undefined ? DEFAULT_STYLE.fillOpacity : s.fillOpacity) as number)
    .toHex();

  const rules = [
    `background-color:${backgroundColor}`,
    s?.underlineThickness ? `text-decoration:underline` : undefined,
    s?.underlineColor ? `text-decoration-color:${s.underlineColor}` : undefined,
    s?.underlineOffset ? `text-underline-offset:${s.underlineOffset}px` : undefined,
    s?.underlineThickness ? `text-decoration-thickness:${s.underlineThickness}px` : undefined
  ].filter(Boolean);

  return rules.join(';');
}

/**
 * EXPERIMENTAL!
 */
export const createCSSHighlightPainter = (): Painter => {
  const elem = document.createElement('style');
  document.getElementsByTagName('head')[0].appendChild(elem);

  let currentRendered = new Set<string>();

  const redraw = (
    highlights: Highlight[], 
    _: ViewportBounds,
    currentStyle?: HighlightStyleExpression,
    __?: Map<string, HighlightStyleExpression>,
    ___?: boolean
  ) => {
    // Next set of rendered annotation IDs and selections
    const nextRendered = new Set(highlights.map(h => h.annotation.id));

    // Annotations currently in this stylesheet that no longer need rendering
    const toRemove = Array.from(currentRendered).filter(id => !nextRendered.has(id));

    // For simplicity, re-generate the whole stylesheet
    const updatedCSS = highlights.map(h => {
      const base = currentStyle
        ? typeof currentStyle === 'function'
          ? currentStyle(h.annotation, h.state)
          : currentStyle
        : h.state?.selected ? DEFAULT_SELECTED_STYLE : DEFAULT_STYLE;

      // Trigger the custom painter (if any) as a side-effect
      const style = computeStyle(h, currentStyle);

      return `::highlight(_${h.annotation.id}) { ${toCSS(style)} }`;
    });

    elem.innerHTML = updatedCSS.join('\n');

    // After we have the styles, we need to update the Highlights.
    // Note that the (experimental) CSS Custom Highlight API is not yet
    // available in TypeScript!

    // @ts-ignore
    CSS.highlights.clear();
    // toRemove.forEach(id => CSS.highlights.delete(`_${id}`));

    // Could be improved further by (re-)setting only annotations that
    // have changes.
    highlights.forEach(({ annotation }) => {
      const ranges = annotation.target.selector.map((s: RevivedTextSelectorLike) => s.range);

      // @ts-ignore
      const highlights = new Highlight(...ranges);

      // @ts-ignore
      CSS.highlights.set(`_${annotation.id}`, highlights);
    });

    currentRendered = nextRendered;
  }

  const setVisible = (visible: boolean) => {
    console.log('setVisible not implemented on CSS Custom Highlights renderer');
  }

  const destroy = () => {
    // Clear all highlights from the Highlight Registry
    // @ts-ignore
    CSS.highlights.clear();

    // Remove the stylesheet
    elem.remove();
  }

  return {
    destroy,
    setVisible,
    redraw
  };

}

export const createCSSHighlightRenderer: RendererFactory<TextAnnotationLike> = (
  container: HTMLElement,
  state: TextAnnotatorState<TextAnnotationLike, any>,
  viewport: ViewportState
) => createRenderer(createCSSHighlightPainter(), container, state, viewport);